import http from '@/services/http';
import { Order, OrderComment, OrderFilters, Quote } from '@/interfaces/order.interface';
import { OrderDocument } from '@/interfaces/document.interface';
import { VuetifyPaginationInterface } from '@/interfaces/vuetify-pagination.interface';

export default {
  async getAll(pagination: VuetifyPaginationInterface, filters: OrderFilters) {
    const { search, shipperCountryName, consigneeCountryName, status, priority, shipperGroup, logistics, createdAtRange } = filters;

    const queryParams = [
      `page=${pagination.page}`,
      `itemsPerPage=${pagination.itemsPerPage}`,
      search && `search=${encodeURIComponent(search)}`,
      shipperCountryName && `shipperCountryName=${encodeURIComponent(shipperCountryName)}`,
      consigneeCountryName && `consigneeCountryName=${encodeURIComponent(consigneeCountryName)}`,
      status && status.length ? `status=${status.map(s => s.uuid).join(',')}` : '',
      priority && priority.uuid ? `priority=${priority.uuid}` : '',
      shipperGroup && shipperGroup.uuid ? `shipperGroup=${shipperGroup.uuid}` : '',
      logistics && logistics.uuid ? `logistics=${logistics.uuid}` : '',
      createdAtRange && createdAtRange.length > 0
        ? `createdAtStart=${createdAtRange[0]}&createdAtEnd=${createdAtRange[createdAtRange.length > 1 ? 1 : 0]}`
        : '',
    ].filter(Boolean).join('&');

    const response = await http.get(`/orders?${queryParams}`);
    return response.data;
  },
  async getOne(orderUuid: string) {
    const response = await http.get(`/orders/${orderUuid}`);
    return response.data;
  },
  async fetchStatuses() {
    const response = await http.get(`/orders/statuses`);
    return response.data;
  },
  async fetchPriorities() {
    const response = await http.get(`/orders/priorities`);
    return response.data;
  },
  async fetchTransportModes() {
    const response = await http.get(`/orders/transport_modes`);
    return response.data;
  },
  async create(order: Order) {
    const response = await http.post(`/orders`, order);
    return response.data;
  },
  async update(order: Order) {
    const response = await http.put(`/orders/${order.uuid}`, order);
    return response.data;
  },
  async delete(uuid: string) {
    const response = await http.delete(`/orders/${uuid}`);
    return response.data;
  },
  async fetchHistory(orderUuid: string) {
    const response = await http.get(`/orders/${orderUuid}/history`);
    return response.data;
  },
  async fetchQuotes(orderUuid: string) {
    const response = await http.get(`/orders/${orderUuid}/quotes`);
    return response.data;
  },
  async createQuote(orderUuid: string, quote: Quote) {
    const response = await http.post(`/orders/${orderUuid}/quotes`, quote);
    return response.data;
  },
  async updateQuote(quote: Quote) {
    const response = await http.put(`/quotes/${quote.uuid}`, quote);
    return response.data;
  },
  async deleteQuote(quoteUuid: string) {
    const response = await http.delete(`/quotes/${quoteUuid}`);
    return response.data;
  },
  async fetchComments(orderUuid: string) {
    const response = await http.get(`/orders/${orderUuid}/comments`);
    return response.data;
  },
  async createComment(orderUuid: string, comment: string) {
    const response = await http.post(`/orders/${orderUuid}/comments`, {comment});
    return response.data;
  },
  async updateComment(comment: OrderComment) {
    const response = await http.put(`/comments/${comment.uuid}`, comment);
    return response.data;
  },
  async deleteComment(commentUuid: string) {
    const response = await http.delete(`/comments/${commentUuid}`);
    return response.data;
  },
  async fetchDocuments(orderUuid: string) {
    const response = await http.get(`/orders/${orderUuid}/documents`);
    return response.data;
  },
  async createDocument(orderUuid: string, document: OrderDocument) {
    const formData = new FormData();
    formData.append('file', document.file);
    const response = await http.post(
        `/orders/${orderUuid}/documents`,
        formData
    );
    return response.data;
  },
  async updateDocument(document: OrderDocument) {
    const response = await http.put(`/documents/${document.uuid}`, {document});
    return response.data;
  },
  async deleteDocument(commentUuid: string) {
    const response = await http.delete(`/documents/${commentUuid}`);
    return response.data;
  },
  async rankUp(orderUuid: string) {
    const response = await http.put(`/orders/${orderUuid}/rank`, {
      rank: 1
    });
    return response.data;
  },
  async rankDown(orderUuid: string) {
    const response = await http.put(`/orders/${orderUuid}/rank`, {
      rank: -1
    });
    return response.data;
  }
};
